import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient.js';

const corridors = [
  'Okanagan South',
  'Okanagan North',
  'Nicola/Merritt',
  'Kootenays',
  'Cariboo/North',
  'Lower Mainland',
];

const inputClass =
  'w-full px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';

const EditRouteModal = ({ route, items, onClose, onSaved }) => {
  const [form, setForm] = useState({
    route_name: route.route_name || '',
    corridor: route.corridor || '',
    start_date: route.start_date || '',
    notes: route.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [warningAcked, setWarningAcked] = useState(false);

  const startDateChanged = form.start_date !== (route.start_date || '');
  const maxDayNumber = Math.max(...items.filter((i) => i.day_number).map((i) => i.day_number), 0);
  const hasPinnedItems = items.some((i) => i.is_pinned);

  // Reset warning state when start_date changes
  useEffect(() => {
    setWarning(null);
    setWarningAcked(false);
    setError(null);
  }, [form.start_date]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.route_name.trim()) {
      setError('Route name is required.');
      return;
    }

    setSaving(true);
    setError(null);

    // 1. Applicator overlap check
    if (startDateChanged && route.applicator_id && form.start_date && maxDayNumber > 0) {
      const newStart = new Date(form.start_date + 'T00:00:00');
      const newEnd = new Date(form.start_date + 'T00:00:00');
      newEnd.setDate(newEnd.getDate() + maxDayNumber - 1);

      const { data: otherRoutes } = await supabase
        .from('routes')
        .select('id, route_name, start_date')
        .eq('applicator_id', route.applicator_id)
        .neq('id', route.id)
        .in('status', ['draft', 'active'])
        .not('start_date', 'is', null);

      if (otherRoutes && otherRoutes.length > 0) {
        const { data: otherItems } = await supabase
          .from('route_items')
          .select('route_id, day_number')
          .in('route_id', otherRoutes.map((r) => r.id));

        const maxDays = {};
        for (const item of otherItems || []) {
          if (!maxDays[item.route_id] || item.day_number > maxDays[item.route_id]) {
            maxDays[item.route_id] = item.day_number;
          }
        }

        for (const other of otherRoutes) {
          const otherMax = maxDays[other.id] || 5;
          const otherStart = new Date(other.start_date + 'T00:00:00');
          const otherEnd = new Date(other.start_date + 'T00:00:00');
          otherEnd.setDate(otherEnd.getDate() + otherMax - 1);

          if (newStart <= otherEnd && newEnd >= otherStart) {
            const fmt = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
            setError(
              `Blocked: "${other.route_name}" runs ${fmt(otherStart)}\u2013${fmt(otherEnd)} for the same applicator. Change the applicator assignment or pick non-overlapping dates.`
            );
            setSaving(false);
            return;
          }
        }
      }
    }

    // 2. Pinned items warning
    if (startDateChanged && hasPinnedItems && !warningAcked) {
      setWarning(
        'This route has pinned jobs. Changing the start date will shift their calendar dates.'
      );
      setSaving(false);
      return;
    }

    // 3. Save
    const payload = {
      route_name: form.route_name.trim(),
      corridor: form.corridor || null,
      start_date: form.start_date || null,
      notes: form.notes || null,
    };

    if (startDateChanged && form.start_date) {
      payload.needs_travel_calc = true;
    }

    const { error: updateError } = await supabase
      .from('routes')
      .update(payload)
      .eq('id', route.id);

    if (updateError) {
      setError(`Save failed: ${updateError.message}`);
      setSaving(false);
      return;
    }

    onSaved(payload);
  };

  const handleWarningAck = () => {
    setWarningAcked(true);
    setWarning(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Edit Route</h3>

        {error && (
          <div className="bg-red-500/20 border border-red-500/40 text-red-200 rounded p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {warning && (
          <div className="bg-amber-400/10 border border-amber-400/30 text-amber-300 rounded p-3 mb-4 text-sm">
            <p className="mb-3">{warning}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setWarning(null); }}
                className="px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleWarningAck}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-700 text-white hover:bg-green-600 transition-colors"
              >
                Save Anyway
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Route Name</label>
            <input
              type="text"
              value={form.route_name}
              onChange={set('route_name')}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Corridor</label>
            <select value={form.corridor} onChange={set('corridor')} className={inputClass}>
              <option value="">None</option>
              {corridors.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Start Date</label>
            <input
              type="date"
              value={form.start_date}
              onChange={set('start_date')}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={set('notes')}
              rows={2}
              className={inputClass}
            />
          </div>

          {!warning && (
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-md bg-green-700 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditRouteModal;
